/**
 * Where the organization stands on each of its plan's limits, for everyone in it (admins change the plan on the Plan
 * page): the plan and its dates, then people, spaces, storage (per space, and what only history holds) and the email
 * bucket, each with a bar against its limit.
 */
import type { ComponentChildren } from "preact";
import { api } from "../api/client.ts";
import type { OrgUsage, SpaceOut, Standing } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { tierName } from "../lib/billing.ts";
import { formatBytes, formatInstant, formatRelative } from "../lib/format.ts";
import { orgApi, spaceUrl } from "../lib/paths.ts";
import { useResource } from "../lib/resource.ts";
import type { PageProps } from "../lib/state.ts";
import { Link } from "../ui/Button.tsx";
import { Async, Badge, Section } from "../ui/misc.tsx";
import { LINK } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";
import { DATE, planDates, STATES } from "./settings/planState.ts";

/** Used, as a share of the limit, from which a bar turns amber (and red when it's all used). */
const NEAR = 0.8;
const PERCENT = 100;

/** A bar of `used` against `limit` (none: no limit, no bar): every bar fills with what's used. */
function Meter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const share = Math.min(1, Math.max(0, used / Math.max(1, limit)));
  const color = share >= 1 ? "bg-red-600" : share >= NEAR ? "bg-amber-500" : "bg-blue-600";
  return (
    <div
      // The numbers are in the text beside it: the bar only pictures them.
      aria-hidden="true"
      data-meter={label}
      data-share={Math.round(share * PERCENT)}
      class="mt-1 h-2 w-full max-w-md overflow-hidden rounded-full bg-zinc-100"
    >
      <div class={`h-full rounded-full ${color}`} style={{ width: `${share * PERCENT}%` }} />
    </div>
  );
}

/** One limit: its name, the numbers, its bar, and what else there is to say. */
function Row(p: { name: string; numbers: string; meter?: ComponentChildren; children?: ComponentChildren }) {
  return (
    <li class="flex flex-col gap-1 py-3">
      <div class="flex max-w-md flex-wrap items-baseline justify-between gap-2">
        <span class="font-medium text-zinc-900">{p.name}</span>
        <span class="text-zinc-700 tabular-nums">{p.numbers}</span>
      </div>
      {p.meter}
      {p.children}
    </li>
  );
}

/** "3 of 5", or just "3" without a limit. */
const of = (used: number | string, limit: number | string | undefined) =>
  limit === undefined ? t("usage.noLimit", { used }) : t("plan.of", { used, limit });

/** The trial's or the end's dates, as they matter in each state. */
function stateDates(st: Standing): string[] {
  const at = (iso: string | null | undefined) => (iso ? formatInstant(iso, DATE) : "");
  switch (st.state) {
    case "trial":
      return st.trial_ends_at ? [t("plan.trialEnds", { at: at(st.trial_ends_at) })] : [];
    case "grace":
      return [t("usage.ended", { at: at(st.ended_at) }), t("usage.mailUntil", { at: at(st.mail_until) })];
    case "suspended":
      return [
        t("usage.ended", { at: at(st.ended_at) }),
        t("usage.mailRefused", { at: at(st.mail_until) }),
        t("usage.keptUntil", { at: at(st.kept_until) }),
      ];
    default:
      return [];
  }
}

/**
 * The email bucket: how much of it is used (taken, not refilled yet), like every other limit; what's available now,
 * how fast it refills, when it's full (or has room) again.
 */
function Emails({ st }: { st: Standing }) {
  const { limits: l, usage: u } = st;
  if (!l) {
    return <Row name={t("usage.emails")} numbers={t("usage.available", { available: u.emails_available })} />;
  }
  const used = Math.max(0, l.emails - u.emails_available);
  const notes = [
    t("usage.available", { available: u.emails_available }),
    t("usage.refill", { rate: l.emails_per_day }),
    u.emails_room_at ? t("usage.roomIn", { in: formatRelative(u.emails_room_at) }) : null,
    u.emails_full_at ? t("usage.fullIn", { in: formatRelative(u.emails_full_at) }) : t("usage.full"),
  ];
  return (
    <Row
      name={t("usage.emails")}
      numbers={of(used, l.emails)}
      meter={<Meter label={t("usage.emails")} used={used} limit={l.emails} />}
    >
      <p class="text-zinc-600">{notes.filter(Boolean).join(" · ")}</p>
    </Row>
  );
}

function UsagePage({ space, u }: { space: SpaceOut; u: OrgUsage }) {
  const st = u.standing;
  const { limits: l, usage } = st;
  const [stateName, stateTone] = STATES[st.state];
  return (
    <>
      <Section title={t("billing.current")}>
        <div class="flex max-w-xl flex-col gap-1 text-sm">
          <p class="flex flex-wrap items-center gap-2">
            <span class="text-base font-semibold text-zinc-900">
              {u.plan ? tierName(u.plan.tier) : t("billing.none")}
            </span>
            {st.trial_ends_at ? <Badge tone={stateTone}>{t(stateName)}</Badge> : null}
          </p>
          {[...(u.plan ? [planDates(u.plan)] : []), ...stateDates(st)].flatMap((d) =>
            d
              ? [
                  <p key={d} class="text-zinc-600">
                    {d}
                  </p>,
                ]
              : [],
          )}
          <Link href={spaceUrl(space, "/settings/billing")} class={`${LINK} mt-1`}>
            {t("billing.see")}
          </Link>
        </div>
      </Section>
      <Section title={t("plan.usage")}>
        <ul class="divide-y divide-zinc-100 text-sm">
          <Row
            name={t("plan.people")}
            numbers={of(usage.people, l?.people)}
            meter={l ? <Meter label={t("plan.people")} used={usage.people} limit={l.people} /> : null}
          >
            {u.others > 0 ? <p class="text-zinc-600">{t("usage.others", { count: u.others })}</p> : null}
          </Row>
          <Row
            name={t("plan.spaces")}
            numbers={of(usage.spaces, l?.spaces)}
            meter={l ? <Meter label={t("plan.spaces")} used={usage.spaces} limit={l.spaces} /> : null}
          />
          <Row
            name={t("plan.storage")}
            numbers={of(formatBytes(usage.storage), l ? formatBytes(l.storage) : undefined)}
            meter={l ? <Meter label={t("plan.storage")} used={usage.storage} limit={l.storage} /> : null}
          >
            <ul class="mt-1 flex max-w-md flex-col gap-0.5">
              {u.spaces.map((s) => (
                <li key={s.id} class="flex justify-between gap-2">
                  <Link href={spaceUrl(s, "")} class={LINK}>
                    {s.name}
                  </Link>
                  <span class="text-zinc-600 tabular-nums">{formatBytes(s.storage)}</span>
                </li>
              ))}
            </ul>
            {u.history_storage > 0 ? (
              <p class="text-zinc-600">{t("usage.history", { size: formatBytes(u.history_storage) })}</p>
            ) : null}
          </Row>
          <Emails st={st} />
        </ul>
      </Section>
    </>
  );
}

/** The organization's use of its plan's limits, in detail. */
export function Usage({ space }: PageProps) {
  const usage = useResource(`usage:${space.org}`, (signal) =>
    api.get<OrgUsage>(orgApi(space, "/usage"), undefined, signal),
  );
  return (
    <>
      <SettingsNav space={space} tab="/usage" />
      <Async r={usage}>{(u) => <UsagePage space={space} u={u} />}</Async>
    </>
  );
}
